import { inject, Injectable } from '@angular/core';
import { EnvironmentService, type JsonLdListElementModel } from '@big-direkt/utils/environment';
import { type BreadcrumbItemModel, type MetadataModel, type NodeModel } from '@big-direkt/utils/shared';
import { type JsonLd } from '../models/json-ld';
import { type SocialMetaTag } from '../models/social-meta-tag';
import { JsonLdService } from './json-ld.service';
import { SeoSocialShareService } from './seo-social-share.service';

@Injectable({
    providedIn: 'root',
})
export class SeoService {
    private readonly jsonLdService = inject(JsonLdService);
    private readonly seoSocialShareService = inject(SeoSocialShareService);
    private readonly environmentService = inject(EnvironmentService);

    public initializeNode(node: NodeModel): void {
        const jsonLd: JsonLd[] = [];

        const { heading, introduction, created, changed } = node;

        if (heading ?? introduction ?? created ?? changed) {
            jsonLd.push(
                this.getJsonLd('Article', {
                    name: heading ?? '',
                    headline: heading ?? '',
                    description: introduction ?? '',
                    datePublished: created ?? '',
                    dateModified: changed ?? '',
                }),
            );
        }

        const breadcrumb: JsonLd = this.generateBreadcrumb(node);

        if (Object.keys(breadcrumb).length) {
            jsonLd.push(breadcrumb);
        }

        this.setJsonLd(jsonLd);

        if (node.metadata) {
            this.setMetadata(node.metadata);
        }
    }

    private setMetaTitle(item: MetadataModel): void {
        this.seoSocialShareService.setTitle(item.attributes.content);
    }

    private setMetaCanonical(item: MetadataModel): void {
        this.seoSocialShareService.setCanonicalUrl(item.attributes.href);
        this.seoSocialShareService.setUrl(item.attributes.href);
        this.seoSocialShareService.setMetaTag({ property: 'pin:url', content: item.attributes.href });
    }

    private setJsonLd(jsonLdObject: JsonLd[]): void {
        this.jsonLdService.setData({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '@context': 'https://schema.org',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '@graph': [...jsonLdObject, ...this.defaultData()],
        });
    }

    private getJsonLd(type: string, rawData?: JsonLd, context?: string): JsonLd {
        return this.jsonLdService.getObject(type, rawData, context);
    }

    private setMetadata(items: MetadataModel[]): void {
        [...(this.environmentService.metaTags ?? []), ...items].forEach((item: MetadataModel): void => {
            const { content, href, name, property, rel } = item.attributes;

            if (name === 'title') {
                this.setMetaTitle(item);

                return;
            }

            if (name === 'description') {
                this.seoSocialShareService.setDescription(content);

                return;
            }

            if (rel === 'canonical') {
                this.setMetaCanonical(item);

                return;
            }

            if (rel === 'image_src') {
                this.seoSocialShareService.setImage(href);

                return;
            }

            const metaTag: SocialMetaTag = { content };

            if (property) {
                metaTag.property = property;
            }

            if (name) {
                metaTag.name = name;
            }

            this.seoSocialShareService.setMetaTag(metaTag);
        });
    }

    private defaultData(): JsonLd[] {
        const itemListElement: JsonLd[] = [];
        const jsonLd: EnvironmentService['jsonLd'] = this.environmentService.jsonLd;

        if (!jsonLd) {
            return [];
        }

        jsonLd.owns.itemListElement.forEach((item: JsonLdListElementModel): void => {
            itemListElement.push(this.getJsonLd('MobileApplication', item as unknown as JsonLd));
        });

        return [
            this.getJsonLd('Organization', {
                ...jsonLd.organization,
                potentialAction: {
                    ...this.getJsonLd('potentialAction', jsonLd.potentialAction),
                },
                contactPoint: {
                    ...this.getJsonLd('contactPoint', jsonLd.contactPoint),
                },
                address: {
                    ...this.getJsonLd('PostalAddress', jsonLd.address),
                },
                sameAs: jsonLd.sameAs,
                owns: {
                    ...this.getJsonLd('ItemList', {
                        ...jsonLd.owns,
                        numberOfItems: String(itemListElement.length),
                        itemListElement,
                    }),
                },
            }),

            this.getJsonLd('WebSite', {
                url: jsonLd.organization.url,
                potentialAction: {
                    ...this.getJsonLd('potentialAction', jsonLd.potentialAction),
                },
            }),
        ];
    }

    private generateBreadcrumb(node: NodeModel): JsonLd {
        const { metadata, breadcrumbs } = node;
        const itemListElement: JsonLd[] = [];

        if (!metadata || !breadcrumbs) {
            return {};
        }

        const canonical: MetadataModel | undefined = metadata.find((item: MetadataModel): boolean => item.attributes.rel === 'canonical');

        if (canonical?.attributes.href === undefined) {
            return {};
        }

        const url: URL = new URL(canonical.attributes.href);
        const lastItemIndex: number = breadcrumbs.length - 1;

        breadcrumbs.forEach((item: BreadcrumbItemModel, index: number) => {
            itemListElement.push({
                // eslint-disable-next-line @typescript-eslint/naming-convention
                '@type': 'ListItem',
                position: String(index + 1),
                name: item.title,
                item: `${url.origin}${lastItemIndex === index ? url.pathname : item.uri}`,
            });
        });

        return this.getJsonLd('BreadcrumbList', {
            itemListElement,
        });
    }
}
